import { Dispatch, SetStateAction, createContext, useEffect, useMemo, useState } from "react";
import { GroupedEventModel } from "../../models/Event";
import { fetchAPI } from "../../Helpers/utils";

//----------------------------------------
// CONTEXT
export type EventContextType = {
  categories: string[],
  events: GroupedEventModel[],
  filters: {
    category: string,
    searchTerm: string
  },
  totalRecords: number,

  categorySetter: Dispatch<SetStateAction<string>>,
  termSetter: Dispatch<SetStateAction<string>>
}

export const EventContext = createContext<EventContextType | null>(null);

//----------------------------------------
// PROVIDER
export type EventProviderModel = {
  source: number,
  pageSize: number,
  numberOfMonths: number,
  children: React.ReactNode
}

export default function EventProvider({ children, source, pageSize, numberOfMonths}: EventProviderModel) {
  const [categories, setCategories] = useState<string[]>([]);
  const [events, setEvents] = useState<GroupedEventModel[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // get data
  useEffect(() => {
    async function getCategories() {
      const data = await fetchAPI<string[]>(`/api/event/categories`);
      setCategories(data);
    }

    getCategories();
  }, []);

  // get events
  useEffect(() => {
    async function getEvents() {
      let url = `/api/event/${source}/list?pageSize=${pageSize}`;
      if (selectedCategory.length) {
        url += `&category=${selectedCategory}`;
      }
      if (searchTerm.length) {
        url += `&searchTerm=${searchTerm}`;
      }
      setEvents(await fetchAPI<GroupedEventModel[]>(url));
    }

    getEvents();
  }, [source, pageSize, selectedCategory, searchTerm])

  const eventContext = useMemo(() => {
    return {
      categories,
      events,
      filters: {
        category: selectedCategory,
        searchTerm: searchTerm
      },
      totalRecords: events?.length,

      categorySetter: setSelectedCategory,
      termSetter: setSearchTerm
    }
  }, [
    categories,
    events,
    selectedCategory,
    searchTerm,

    setSelectedCategory,
    setSearchTerm
  ]);

  return (
    <EventContext.Provider value={eventContext}>
      {children}
    </EventContext.Provider>
  )
}